import { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Loader2, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

export const ItineraryLine = ({ line }) => {
  if (/^Day\s+\d/i.test(line)) {
    return (
      <div className="mt-8 first:mt-0">
        <div className="gold-hairline mb-4 w-full" />
        <h4 className="font-serif text-2xl font-light italic tracking-wide text-amber-200">{line}</h4>
      </div>
    );
  }
  if (line.trim().startsWith("•") || line.trim().startsWith("-")) {
    return (
      <div className="mt-2.5 flex items-start gap-3">
        <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-amber-500/70" />
        <p className="text-sm leading-relaxed text-neutral-300 sm:text-base">{line.replace(/^\s*[•-]\s*/, "")}</p>
      </div>
    );
  }
  if (/^Concierge Note/i.test(line)) {
    return (
      <p className="mt-8 border border-amber-500/30 bg-amber-500/5 p-4 font-serif text-base italic text-amber-100/90 sm:text-lg">
        {line}
      </p>
    );
  }
  if (!line.trim()) return null;
  return <p className="mt-3 text-sm leading-relaxed text-neutral-400 sm:text-base">{line}</p>;
};

export const Itinerary = ({ selectedServices, country = "Iceland" }) => {
  const [arrival, setArrival] = useState("");
  const [departure, setDeparture] = useState("");
  const [partySize, setPartySize] = useState("2");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [requests, setRequests] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [shareId, setShareId] = useState("");
  const [saving, setSaving] = useState(false);

  const generate = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error("Please add your name and email so we know whose journey this is.");
      return;
    }
    setLoading(true);
    setText("");
    setShareId("");
    let fullText = "";
    try {
      const res = await fetch(`${API}/itinerary`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          services: selectedServices.map((s) => s.name),
          arrival_date: arrival,
          departure_date: departure,
          party_size: partySize,
          notes: requests,
          country,
        }),
      });
      if (!res.ok || !res.body) throw new Error("Request failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop();
        for (const chunk of chunks) {
          if (!chunk.startsWith("data:")) continue;
          const payload = JSON.parse(chunk.slice(5));
          if (payload.delta) {
            fullText += payload.delta;
            setText(fullText);
          }
          if (payload.error) {
            toast.error(payload.error);
          }
        }
      }
      if (fullText.trim()) {
        setSaving(true);
        try {
          const saveRes = await fetch(`${API}/itineraries`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name,
              email,
              services: selectedServices.map((s) => s.name),
              arrival_date: arrival,
              departure_date: departure,
              party_size: partySize,
              text: fullText,
            }),
          });
          if (saveRes.ok) {
            const saved = await saveRes.json();
            setShareId(saved.id);
          }
        } catch {
          toast.error("Itinerary composed, but the private link could not be created.");
        } finally {
          setSaving(false);
        }
      }
    } catch (e) {
      toast.error("The concierge is momentarily unavailable. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="itinerary" data-testid="itinerary-section" className="relative overflow-hidden py-24 sm:py-36">
      <div className="absolute left-1/2 top-0 h-[400px] w-[700px] -translate-x-1/2 rounded-full bg-amber-500/5 blur-[160px]" />
      <div className="relative mx-auto max-w-4xl px-5 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.35em] text-amber-300/80">The AI Concierge</p>
          <h2 className="mt-4 font-luxe text-2xl uppercase leading-[1.2] tracking-[0.12em] text-neutral-50 sm:text-4xl">
            A day-by-day plan, <span className="font-serif normal-case italic text-amber-200">composed instantly.</span>
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-neutral-400">
            Our concierge intelligence drafts a first itinerary from the experiences on your list —
            refined by a human before anything is booked.
          </p>
        </motion.div>

        <motion.div
          className="mt-12 border border-amber-500/20 bg-[#0c0c10]/80 p-6 backdrop-blur-xl sm:p-10"
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.1 }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">Your Name *</span>
              <input
                data-testid="itinerary-name-input"
                placeholder="So we know whose journey this is"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-2 w-full border border-amber-900/40 bg-[#0d0d12] px-4 py-3 text-sm text-amber-100 placeholder:text-neutral-600 outline-none transition-colors focus:border-amber-400"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">Your Email *</span>
              <input
                type="email"
                data-testid="itinerary-email-input"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full border border-amber-900/40 bg-[#0d0d12] px-4 py-3 text-sm text-amber-100 placeholder:text-neutral-600 outline-none transition-colors focus:border-amber-400"
              />
            </label>
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">Arrival</span>
              <input
                type="date"
                data-testid="itinerary-arrival-input"
                value={arrival}
                onChange={(e) => setArrival(e.target.value)}
                className="mt-2 w-full border border-amber-900/40 bg-[#0d0d12] px-4 py-3 text-sm text-amber-100 outline-none transition-colors focus:border-amber-400"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">Departure</span>
              <input
                type="date"
                data-testid="itinerary-departure-input"
                value={departure}
                onChange={(e) => setDeparture(e.target.value)}
                className="mt-2 w-full border border-amber-900/40 bg-[#0d0d12] px-4 py-3 text-sm text-amber-100 outline-none transition-colors focus:border-amber-400"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">Party Size</span>
              <input
                type="number"
                min="1"
                max="100"
                data-testid="itinerary-party-input"
                value={partySize}
                onChange={(e) => setPartySize(e.target.value)}
                className="mt-2 w-full border border-amber-900/40 bg-[#0d0d12] px-4 py-3 text-sm text-amber-100 outline-none transition-colors focus:border-amber-400"
              />
            </label>
          </div>

          <div data-testid="itinerary-selected-summary" className="mt-6 text-xs uppercase tracking-[0.2em] text-neutral-500">
            {selectedServices.length > 0 ? (
              <span>
                Composing for <span className="text-amber-200/90">{country}</span> from:{" "}
                <span className="text-amber-200/90">{selectedServices.map((s) => s.name).join(" · ")}</span>
              </span>
            ) : (
              <span>No experiences selected — a signature {country} highlights plan will be composed.</span>
            )}
          </div>

          <label className="mt-6 block">
            <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">
              Special requests — where you want to go, what you want to do
            </span>
            <textarea
              data-testid="itinerary-requests-input"
              rows={3}
              placeholder="In your own words — 'a glacier proposal at sunset', 'my father's 60th', 'whale watching if weather allows' — the concierge weaves every word into your days."
              value={requests}
              onChange={(e) => setRequests(e.target.value)}
              className="mt-2 w-full resize-none border border-amber-900/40 bg-[#0d0d12] px-4 py-3 text-sm text-amber-100 placeholder:text-neutral-600 outline-none transition-colors focus:border-amber-400"
            />
          </label>

          <button
            data-testid="generate-itinerary-button"
            onClick={generate}
            disabled={loading}
            className="mt-8 flex w-full items-center justify-center gap-3 bg-amber-500 px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors duration-300 hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            {loading ? "Composing your days..." : "Generate My Itinerary"}
          </button>

          {(text || loading) && (
            <div data-testid="itinerary-output" className="mt-10 border-t border-amber-500/15 pt-8">
              <div className="mb-6 flex items-center justify-between">
                <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">
                  <Sparkles className="h-3.5 w-3.5" />
                  Draft Itinerary
                </span>
                {text && !loading && (
                  <button
                    data-testid="copy-itinerary-button"
                    onClick={() => {
                      navigator.clipboard.writeText(text);
                      toast.success("Itinerary copied to clipboard");
                    }}
                    className="flex items-center gap-2 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
                  >
                    <Copy className="h-3 w-3" />
                    Copy
                  </button>
                )}
              </div>
              {saving && !shareId && (
                <p className="mb-6 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                  <Loader2 className="h-3 w-3 animate-spin text-amber-400" /> Sealing your private link...
                </p>
              )}
              {shareId && (
                <div data-testid="share-link-box" className="mb-6 flex flex-col gap-3 border border-amber-500/30 bg-amber-500/5 p-4 sm:flex-row sm:items-center">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-300/80">
                    Private link — ready to share
                  </p>
                  <div className="flex flex-1 items-center gap-2">
                    <input
                      readOnly
                      data-testid="share-link-input"
                      value={`${window.location.origin}/i/${shareId}`}
                      onFocus={(e) => e.target.select()}
                      className="w-full border border-amber-900/40 bg-[#0d0d12] px-3 py-2 text-xs text-amber-100 outline-none"
                    />
                    <button
                      data-testid="copy-share-link-button"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/i/${shareId}`);
                        toast.success("Private link copied");
                      }}
                      className="shrink-0 border border-amber-500/50 px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
                    >
                      Copy Link
                    </button>
                  </div>
                </div>
              )}
              {text.split("\n").map((line, i) => (
                <ItineraryLine key={i} line={line} />
              ))}
              {text && !loading && (
                <p data-testid="itinerary-disclaimer" className="mt-8 border-t border-neutral-800/60 pt-4 text-[9px] leading-relaxed tracking-[0.08em] text-neutral-600">
                  There can be mistakes in pricing. This never happens, but if it does, we do not take responsibility if pricing is wrong.
                  There is a 7 day cancellation policy on everything after it is confirmed with 0% refunded unless we can get money back from the tour operator.
                </p>
              )}
              {loading && (
                <span className="mt-4 inline-block h-5 w-2 animate-pulse bg-amber-400/80" />
              )}
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};
